import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { FaTable, FaThLarge } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../config/axios";
import ProgressTrackerModal from "./ProgressTrackerModal";
import ProgressReportModal from "./ProgressReportModal";
import { useRole } from "../context/RoleContext";
import {
  compareBaseTradeNumberDesc,
  progressReportTradeLabel,
} from "../utils/progressReportUtils";

function tradeLabel(t) {
  return progressReportTradeLabel(t);
}

const tradeSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    fontSize: "0.75rem",
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#1d4ed8" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, fontSize: "0.75rem", zIndex: 20 }),
};

const ProgressTrackerCard = () => {
  const navigate = useNavigate();
  const { isBroker } = useRole();
  const [trades, setTrades] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const loadTrades = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axiosInstance.get("/trades");
      setTrades(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Could not load trades for progress tracker.");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  const tradeOptions = useMemo(
    () =>
      trades
        .map((t) => ({
          value: t._id,
          label: tradeLabel(t),
          sortKey: String(t.tradeNumber ?? t.keyInfo?.tradeNumber ?? "").trim(),
        }))
        .sort((a, b) => compareBaseTradeNumberDesc(a.sortKey, b.sortKey)),
    [trades],
  );

  const selectedTradeOption = useMemo(
    () => tradeOptions.find((o) => o.value === selectedId) || null,
    [tradeOptions, selectedId],
  );

  const handleTradeSelect = (option) => {
    const id = option?.value || "";
    setSelectedId(id);
    if (id) setModalOpen(true);
    else setModalOpen(false);
  };

  const handleOpenClick = () => {
    if (selectedId) setModalOpen(true);
  };

  const handleProgressReport = async () => {
    setReportLoading(true);
    setReportModalOpen(true);
    try {
      const res = await axiosInstance.get("/trades");
      setTrades(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Could not load trades for progress report.");
      setReportModalOpen(false);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="xl:flex-1 bg-indigo-50/80 rounded-xl shadow-sm border border-indigo-100 p-3 min-w-0 flex flex-col">
      <h2 className="text-sm font-semibold mb-1 text-indigo-900">
        Progress Tracker
      </h2>
      <p className="text-[11px] text-gray-500 mb-2">
        {isBroker
          ? "Select a trade to view the checklist. Brokers cannot edit progress."
          : "Select a trade to open the full checklist."}
      </p>

      <label className="block text-xs font-medium text-gray-700 mb-0.5">
        Trade
      </label>
      <Select
        inputId="progress-tracker-trade-select"
        options={tradeOptions}
        value={selectedTradeOption}
        onChange={handleTradeSelect}
        isClearable
        isSearchable
        isDisabled={loadingList}
        placeholder="Search or select a trade…"
        noOptionsMessage={() => "No matching trades"}
        styles={tradeSelectStyles}
        className="mb-2"
        classNamePrefix="progress-tracker-trade"
      />

      {selectedId && !modalOpen && (
        <button
          type="button"
          onClick={handleOpenClick}
          className="w-full py-1.5 rounded-md bg-blue-900 text-white text-xs font-medium hover:bg-blue-950"
        >
          Open progress tracker
        </button>
      )}

      {!selectedId && !loadingList && (
        <p className="text-[11px] text-gray-500 mt-0.5">
          {isBroker
            ? "Choose a trade to view the complete progress checklist."
            : "Choose a trade to view and edit the complete progress checklist."}
        </p>
      )}

      <button
        type="button"
        onClick={handleProgressReport}
        disabled={loadingList}
        className="mt-2 w-full flex items-center justify-center gap-2 py-1.5 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
      >
        <FaTable />
        Progress Report
      </button>
      <button
        type="button"
        onClick={() => navigate("/trade-dashboard")}
        className="mt-1.5 w-full flex items-center justify-center gap-2 py-1.5 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-800 hover:bg-gray-50"
      >
        <FaThLarge />
        Trade dashboard
      </button>

      <ProgressTrackerModal
        isOpen={modalOpen}
        tradeId={selectedId}
        onClose={() => setModalOpen(false)}
      />

      <ProgressReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        trades={trades}
        loading={reportLoading}
        onTradesSaved={async () => {
          await loadTrades();
        }}
      />
    </div>
  );
};

export default ProgressTrackerCard;
